import React, { FC, type ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, Alert, Tab, Tabs } from 'react-bootstrap';
import { CustomButton } from '../../../../../components';
import {
  fetchStandingsExplain,
  type StandingsExplainResponse,
  type StandingsExplainStatsRow,
} from '../../../../../apis/class';
import {
  ClassSeedingEnum,
  ClassTypeEnum,
  ConsolationDrawEnum,
  EliminationDrawEnum,
  GroupStandingsTieBreakerEnum,
} from '../../../../../types/enums';
import {
  GROUP_STANDINGS_TIE_BREAKER_LABEL_KEYS,
  listIncludesTeamPoints,
} from '../../../../../utils/groupStandingsTieBreakers';

function formatStandingsCell(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number' && !Number.isFinite(v)) return '—';
  return String(v);
}

/** API coin-toss note prefix (from group-standings-tie-breaker.service). */
const API_COIN_TOSS_NOTE_PREFIX = 'Random order:';

const TIE_STEP_TOP_HINTS: Partial<Record<GroupStandingsTieBreakerEnum, string>> = {
  [GroupStandingsTieBreakerEnum.TEAM_POINTS]: 'standingsExplain-hintTeamPoints',
  [GroupStandingsTieBreakerEnum.TEAM_WINS]: 'standingsExplain-hintTeamWins',
  [GroupStandingsTieBreakerEnum.TEAM_DRAWS]: 'standingsExplain-hintTeamDraws',
  [GroupStandingsTieBreakerEnum.MATCH_WINS]: 'standingsExplain-hintMatchWins',
  [GroupStandingsTieBreakerEnum.HEAD_TO_HEAD]: 'standingsExplain-hintHeadToHead',
  [GroupStandingsTieBreakerEnum.HEAD_TO_HEAD_TEAM_WINS]: 'standingsExplain-hintHeadToHeadTeamWins',
  [GroupStandingsTieBreakerEnum.HEAD_TO_HEAD_POINTS]: 'standingsExplain-hintHeadToHeadPoints',
  [GroupStandingsTieBreakerEnum.SEEDING]: 'standingsExplain-hintSeeding',
  [GroupStandingsTieBreakerEnum.COIN_TOSS]: 'standingsExplain-hintCoinToss',
  [GroupStandingsTieBreakerEnum.GAME_DIFFERENCE]: 'standingsExplain-hintGameDifference',
};

function parseCoinTossOrderIdsFromNote(note: string): number[] | null {
  const t = note.trim();
  if (!t.startsWith(API_COIN_TOSS_NOTE_PREFIX)) return null;
  const rest = t.slice(API_COIN_TOSS_NOTE_PREFIX.length).trim();
  if (!rest) return null;
  return rest
    .split(/\s*→\s*/)
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n));
}

/** Backend uses NEGATIVE_INFINITY when no seed is set. */
function seedingMetricIsUnset(val: unknown): boolean {
  return (
    val == null ||
    (typeof val === 'number' && (!Number.isFinite(val) || val < -1e100))
  );
}

function tieStepSecondColumnHeader(
  criterion: GroupStandingsTieBreakerEnum,
  t: TranslateFn,
): string {
  switch (criterion) {
    case GroupStandingsTieBreakerEnum.TEAM_POINTS:
      return t('standingsExplain-metricColumnTeamPoints');
    case GroupStandingsTieBreakerEnum.TEAM_WINS:
      return t('standingsExplain-metricColumnTeamWins');
    case GroupStandingsTieBreakerEnum.TEAM_DRAWS:
      return t('standingsExplain-metricColumnTeamDraws');
    case GroupStandingsTieBreakerEnum.MATCH_WINS:
      return t('standingsExplain-metricColumnMatchWins');
    case GroupStandingsTieBreakerEnum.COIN_TOSS:
      return t('standingsExplain-metricColumnCoinToss');
    case GroupStandingsTieBreakerEnum.GAME_DIFFERENCE:
      return t('standingsExplain-metricColumnGameDifference');
    case GroupStandingsTieBreakerEnum.HEAD_TO_HEAD:
      return t('standingsExplain-metricColumnHeadToHead');
    case GroupStandingsTieBreakerEnum.HEAD_TO_HEAD_TEAM_WINS:
      return t('standingsExplain-metricColumnHeadToHeadTeamWins');
    case GroupStandingsTieBreakerEnum.HEAD_TO_HEAD_POINTS:
      return t('standingsExplain-metricColumnHeadToHeadPoints');
    default:
      return t('standingsExplain-metric');
  }
}

type StandingsStatsRow = StandingsExplainStatsRow;

function criterionLabel(
  t: (k: string) => string,
  criterion: GroupStandingsTieBreakerEnum,
): string {
  const key = GROUP_STANDINGS_TIE_BREAKER_LABEL_KEYS[criterion];
  return key ? t(key) : String(criterion);
}

function pointsDiffForRow(row: StandingsStatsRow): unknown {
  if (row.pointsDiff !== undefined) return row.pointsDiff;
  return row.points;
}

function teamLossForRow(row: StandingsStatsRow): unknown {
  return row.teamLoss;
}

function matchLossForRow(row: StandingsStatsRow): unknown {
  if (row.matchLoss !== undefined) return row.matchLoss;
  if (row.countMatches != null && row.matchWin != null) {
    return row.countMatches - row.matchWin;
  }
  return undefined;
}

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

function formatTieStepMetricCell(
  val: unknown,
  criterion: GroupStandingsTieBreakerEnum,
  t: TranslateFn,
): { display: string; title?: string } {
  if (criterion === GroupStandingsTieBreakerEnum.SEEDING) {
    if (
      val == null ||
      (typeof val === 'number' && (!Number.isFinite(val) || val < -1e100))
    ) {
      return {
        display: t('standingsExplain-metricSeedingUnset'),
        title: t('standingsExplain-metricSeedingUnsetTitle'),
      };
    }
    if (typeof val === 'number' && Number.isFinite(val)) {
      const n = -val;
      return {
        display: t('standingsExplain-metricSeedingValue', { n }),
        title: t('standingsExplain-metricSeedingValueTitle', { n }),
      };
    }
  }
  if (
    (criterion === GroupStandingsTieBreakerEnum.HEAD_TO_HEAD ||
      criterion === GroupStandingsTieBreakerEnum.HEAD_TO_HEAD_POINTS ||
      criterion === GroupStandingsTieBreakerEnum.HEAD_TO_HEAD_TEAM_WINS) &&
    (val === 0 || val === -0)
  ) {
    return { display: '0', title: t('standingsExplain-metricH2hZeroTitle') };
  }
  if (typeof val === 'number' && !Number.isFinite(val)) {
    return { display: '—' };
  }
  if (val === null || val === undefined) {
    return { display: '—' };
  }
  return { display: String(val) };
}

function seedingTypeLabel(
  t: TranslateFn,
  seedingType: number | string | undefined | null,
): string {
  if (seedingType === undefined || seedingType === null || seedingType === '') return '—';
  const n = Number(seedingType);
  if (!Number.isFinite(n)) return t('standingsExplain-seeding-unknown', { value: String(seedingType) });
  if (n === ClassSeedingEnum.NORMAL) return t('standingsExplain-seeding-normal');
  if (n === ClassSeedingEnum.SNAKE) return t('standingsExplain-seeding-snake');
  return t('standingsExplain-seeding-unknown', { value: n });
}

function eliminationDrawLabel(t: TranslateFn, raw: number | string | undefined | null): string {
  if (raw === undefined || raw === null || raw === '') return '—';
  const n = Number(raw);
  if (!Number.isFinite(n)) return String(raw);
  if (n === EliminationDrawEnum.ALL) return t('standingsExplain-eliminationDraw-all');
  return String(n);
}

function consolationDrawLabel(t: TranslateFn, raw: number | string | undefined | null): string {
  if (raw === undefined || raw === null || raw === '') return '—';
  const n = Number(raw);
  if (!Number.isFinite(n)) return String(raw);
  if (n === ConsolationDrawEnum.ALL) return t('standingsExplain-consolationDraw-allRemaining');
  return String(n);
}

function resolveTieStepEntityName(
  entityId: number,
  nameById: Map<number, string>,
  usePlayer: boolean,
  t: TranslateFn,
): string {
  return nameById.get(entityId) ?? t(usePlayer ? 'standingsExplain-entityLabelPlayer' : 'standingsExplain-entityLabelTeam', { id: entityId });
}

function formatTieSplitBlock(
  block: number[],
  nameById: Map<number, string>,
  usePlayer: boolean,
  t: TranslateFn,
): string {
  return `[${block.map((id) => resolveTieStepEntityName(id, nameById, usePlayer, t)).join(', ')}]`;
}

/** Coin-toss trace from API uses numeric ids separated by " → ". */
function humanizeTieStepNote(
  note: string,
  nameById: Map<number, string>,
  usePlayer: boolean,
  t: TranslateFn,
): string {
  if (!note.startsWith(API_COIN_TOSS_NOTE_PREFIX)) return note;
  const rest = note.slice(API_COIN_TOSS_NOTE_PREFIX.length).trim();
  const parts = rest.split(/\s*→\s*/);
  const mapped = parts.map((segment) => {
    const id = Number(segment.trim());
    if (Number.isFinite(id)) return resolveTieStepEntityName(id, nameById, usePlayer, t);
    return segment;
  });
  return `${API_COIN_TOSS_NOTE_PREFIX} ${mapped.join(' → ')}`;
}

function CoinTossSubgroupBlock({
  note,
  splitInto,
  nameById,
  usePlayer,
  t,
}: {
  note: string;
  splitInto: number[][];
  nameById: Map<number, string>;
  usePlayer: boolean;
  t: TranslateFn;
}): ReactElement {
  const orderIds = parseCoinTossOrderIdsFromNote(note);
  if (!orderIds?.length) {
    return (
      <div className="small text-muted">{humanizeTieStepNote(note, nameById, usePlayer, t)}</div>
    );
  }
  const orderNames = orderIds.map((id) => resolveTieStepEntityName(id, nameById, usePlayer, t));
  const favoredId = splitInto[0]?.[0];
  const favoredName =
    favoredId != null
      ? resolveTieStepEntityName(favoredId, nameById, usePlayer, t)
      : orderNames[0] ?? '';
  return (
    <div className="small">
      <div className="text-muted mb-1">{t('standingsExplain-coinTossOrderLabel')}</div>
      <div className="font-weight-bold text-body mb-1 text-break">{orderNames.join(' → ')}</div>
      {favoredName ? (
        <div className="text-dark">
          {t('standingsExplain-coinTossFavoredWithPosition', {
            name: favoredName,
            total: orderIds.length,
          })}
        </div>
      ) : null}
      <div className="text-muted mt-1" style={{ fontSize: '0.85em' }}>
        {t('standingsExplain-coinTossDeterministic')}
      </div>
    </div>
  );
}

/** Same alignment for th/td; left + nowrap so short numbers sit under headers (not right-floating). */
const ST_RANK = 'text-center text-nowrap align-middle';
const ST_TEAM = 'text-start align-middle text-truncate';
/** Numeric/stat columns: fixed layout + colgroup keeps widths identical across Group1/2/3 tables. */
const ST_STAT = 'text-start text-nowrap align-middle';

const STANDINGS_TABLE_FIXED: React.CSSProperties = { tableLayout: 'fixed', width: '100%' };

function groupStandingsTableStyle(
  showTeamSeriesColumns: boolean,
  showTeamPtsColumn: boolean,
): React.CSSProperties {
  return showTeamSeriesColumns
    ? { ...STANDINGS_TABLE_FIXED, minWidth: showTeamPtsColumn ? '60rem' : '54rem' }
    : STANDINGS_TABLE_FIXED;
}

function groupStandingsColgroup(
  showTeamSeriesColumns: boolean,
  showTeamPtsColumn: boolean,
): ReactElement {
  if (showTeamSeriesColumns) {
    return (
      <colgroup>
        <col style={{ width: '4%' }} />
        <col style={{ width: showTeamPtsColumn ? '18%' : '22%' }} />
        {showTeamPtsColumn ? <col style={{ width: '6%' }} /> : null}
        <col style={{ width: '6%' }} />
        <col style={{ width: '6%' }} />
        <col style={{ width: '6%' }} />
        <col style={{ width: showTeamPtsColumn ? '7%' : '8%' }} />
        <col style={{ width: showTeamPtsColumn ? '7%' : '8%' }} />
        <col style={{ width: showTeamPtsColumn ? '7%' : '8%' }} />
        <col style={{ width: showTeamPtsColumn ? '8%' : '8%' }} />
        <col style={{ width: showTeamPtsColumn ? '12.5%' : '12%' }} />
        <col style={{ width: showTeamPtsColumn ? '12.5%' : '12%' }} />
      </colgroup>
    );
  }
  return (
    <colgroup>
      <col style={{ width: '4%' }} />
      <col style={{ width: '32%' }} />
      <col style={{ width: '8%' }} />
      <col style={{ width: '8%' }} />
      <col style={{ width: '8%' }} />
      <col style={{ width: '10%' }} />
      <col style={{ width: '15%' }} />
      <col style={{ width: '15%' }} />
    </colgroup>
  );
}

function teamSeriesHeaderCells(t: TranslateFn, showTeamPtsColumn: boolean): ReactElement {
  return (
    <>
      {showTeamPtsColumn ? (
        <th className={ST_STAT}>{t('standingsExplain-teamPoints')}</th>
      ) : null}
      <th className={ST_STAT}>{t('standingsExplain-teamWins')}</th>
      <th className={ST_STAT}>{t('standingsExplain-teamDraws')}</th>
      <th className={ST_STAT}>{t('standingsExplain-teamLosses')}</th>
    </>
  );
}

function teamSeriesBodyCells(row: StandingsStatsRow, showTeamPtsColumn: boolean): ReactElement {
  return (
    <>
      {showTeamPtsColumn ? (
        <td className={ST_STAT}>{formatStandingsCell(row.teamPoints)}</td>
      ) : null}
      <td className={ST_STAT}>{formatStandingsCell(row.teamWin)}</td>
      <td className={ST_STAT}>{formatStandingsCell(row.teamDraw)}</td>
      <td className={ST_STAT}>{formatStandingsCell(teamLossForRow(row))}</td>
    </>
  );
}

/** Main / consolation bracket seeding tables: striping + hover for easier scanning. */
const SEEDED_LIST_TABLE_CLASS =
  'table table-sm table-bordered table-striped table-hover small w-100 text-start align-middle';

function seededListTableStyle(
  showTeamSeriesColumns: boolean,
  showTeamPtsColumn: boolean,
): React.CSSProperties {
  return showTeamSeriesColumns
    ? { ...STANDINGS_TABLE_FIXED, minWidth: showTeamPtsColumn ? '64rem' : '58rem' }
    : STANDINGS_TABLE_FIXED;
}

function seededListColgroup(
  showTeamSeriesColumns: boolean,
  showTeamPtsColumn: boolean,
): ReactElement {
  if (showTeamSeriesColumns) {
    return (
      <colgroup>
        <col style={{ width: '4%' }} />
        <col style={{ width: showTeamPtsColumn ? '12%' : '17%' }} />
        <col style={{ width: '9%' }} />
        <col style={{ width: '5%' }} />
        {showTeamPtsColumn ? <col style={{ width: '5%' }} /> : null}
        <col style={{ width: '5%' }} />
        <col style={{ width: '5%' }} />
        <col style={{ width: '5%' }} />
        <col style={{ width: '6%' }} />
        <col style={{ width: '6%' }} />
        <col style={{ width: '6%' }} />
        <col style={{ width: '7%' }} />
        <col style={{ width: '12.5%' }} />
        <col style={{ width: '12.5%' }} />
      </colgroup>
    );
  }
  return (
    <colgroup>
      <col style={{ width: '5%' }} />
      <col style={{ width: '18%' }} />
      <col style={{ width: '12%' }} />
      <col style={{ width: '7%' }} />
      <col style={{ width: '8%' }} />
      <col style={{ width: '8%' }} />
      <col style={{ width: '8%' }} />
      <col style={{ width: '8%' }} />
      <col style={{ width: '13%' }} />
      <col style={{ width: '13%' }} />
    </colgroup>
  );
}

interface StandingsExplainPanelProps {
  classId: number;
}

export const StandingsExplainPanel: FC<StandingsExplainPanelProps> = ({ classId }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payload, setPayload] = useState<StandingsExplainResponse | null>(null);
  const [activeGroupIdx, setActiveGroupIdx] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchStandingsExplain(classId);
      setPayload(data);
      setActiveGroupIdx(0);
    } catch (e: unknown) {
      setPayload(null);
      setError((e as Error)?.message ?? 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [classId]);

  const groupStage = payload?.groupStage;
  const elimination = payload?.elimination;
  const tieBreakers = payload?.tieBreakers;
  const classTypeRaw = payload?.classType;
  const usePlayerLabels = useMemo(() => {
    if (classTypeRaw == null) return false;
    return Number(classTypeRaw) !== ClassTypeEnum.TEAMS;
  }, [classTypeRaw]);

  const colParticipantOrTeam = usePlayerLabels
    ? 'standingsExplain-participant'
    : 'standingsExplain-team';
  const listParticipantsOrTeams = usePlayerLabels
    ? 'standingsExplain-participants'
    : 'standingsExplain-teams';
  const showTeamSeriesColumns = !usePlayerLabels;
  const showTeamPtsColumn =
    showTeamSeriesColumns && listIncludesTeamPoints(tieBreakers?.effective);

  const groups = groupStage?.groups ?? [];
  const activeGroup = groups[activeGroupIdx];

  const effectiveList = useMemo(() => {
    if (!tieBreakers?.effective?.length) return '';
    return tieBreakers.effective.map((c) => criterionLabel(t, c)).join(' → ');
  }, [tieBreakers, t]);

  const teamIdToName = useMemo(() => {
    const m = new Map<number, string>();
    if (!activeGroup) return m;
    for (const row of activeGroup.initialRows ?? []) {
      const id = typeof row.id === 'number' ? row.id : Number(row.id);
      const name = row.name;
      if (Number.isFinite(id) && typeof name === 'string' && name.length > 0) {
        m.set(id, name);
      }
    }
    for (const row of activeGroup.finalRanking ?? []) {
      if (typeof row.name === 'string' && row.name.length > 0) {
        m.set(row.id, row.name);
      }
    }
    return m;
  }, [activeGroup]);

  if (!expanded) {
    return (
      <div className="my-2">
        <CustomButton
          variant="link"
          btnText={t('standingsExplain-show')}
          cb={() => {
            setExpanded(true);
            void load();
          }}
        />
      </div>
    );
  }

  return (
    <div className="my-3 p-3 border rounded bg-light">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="font-weight-bold mb-0">{t('standingsExplain-title')}</h6>
        <div className="d-flex gap-2">
          <CustomButton
            variant="outline-secondary"
            size="sm"
            btnText={loading ? t('standingsExplain-loading') : t('standingsExplain-refresh')}
            cb={() => void load()}
            disabled={loading}
          />
          <CustomButton variant="link" btnText={t('standingsExplain-hide')} cb={() => setExpanded(false)} />
        </div>
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      {!loading && payload && groupStage && !groupStage.supported ? (
        <Alert variant="info">
          {groupStage.message || t('standingsExplain-unsupported')}
        </Alert>
      ) : null}

      {!loading && payload && groupStage?.supported ? (
        <>
          {tieBreakers ? (
            <p className="small text-muted mb-2">
              <strong>{t('standingsExplain-effectiveSource')}:</strong> {tieBreakers.source}
              <br />
              <strong>{t('standingsExplain-effectiveOrder')}:</strong> {effectiveList}
            </p>
          ) : null}

          <Tabs defaultActiveKey="group" id="standings-explain-tabs" className="mb-3">
            <Tab eventKey="group" title={t('standingsExplain-tabGroup')}>
                {groups.length > 1 ? (
                  <div className="mb-2">
                    <label className="small mr-2">{t('standingsExplain-selectGroup')}</label>
                    <select
                      className="form-control form-control-sm d-inline-block w-auto"
                      value={activeGroupIdx}
                      onChange={(e) => setActiveGroupIdx(Number(e.target.value))}
                    >
                      {groups.map((g, idx) => (
                        <option key={g.groupId} value={idx}>
                          {g.groupName}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}

                {activeGroup ? (
                  <>
                    <h6 className="small font-weight-bold">{t('standingsExplain-tieSteps')}</h6>
                    <Accordion>
                      {activeGroup.steps.map((step, idx) => (
                        <Accordion.Item eventKey={String(idx)} key={idx}>
                          <Accordion.Header>
                            {idx + 1}. {criterionLabel(t, step.criterion)}
                          </Accordion.Header>
                          <Accordion.Body>
                            {TIE_STEP_TOP_HINTS[step.criterion] ? (
                              <p className="small text-muted border-bottom pb-2 mb-3">
                                {t(TIE_STEP_TOP_HINTS[step.criterion] as string)}
                              </p>
                            ) : null}
                            {step.subgroups.map((sg, j) => {
                              const tieTeamIds =
                                sg.teamIds.length > 0
                                  ? sg.teamIds
                                  : Object.keys(sg.metricsByTeamId)
                                      .map((k) => Number(k))
                                      .filter((id) => Number.isFinite(id));
                              const coinOrderIds =
                                sg.note && step.criterion === GroupStandingsTieBreakerEnum.COIN_TOSS
                                  ? parseCoinTossOrderIdsFromNote(sg.note)
                                  : null;
                              const coinTossHasDrawOrder = Boolean(coinOrderIds?.length);
                              const allH2hZero =
                                (step.criterion ===
                                  GroupStandingsTieBreakerEnum.HEAD_TO_HEAD ||
                                  step.criterion ===
                                    GroupStandingsTieBreakerEnum.HEAD_TO_HEAD_POINTS ||
                                  step.criterion ===
                                    GroupStandingsTieBreakerEnum.HEAD_TO_HEAD_TEAM_WINS) &&
                                tieTeamIds.length > 0 &&
                                tieTeamIds.every((id) => {
                                  const m = sg.metricsByTeamId[id];
                                  return m === 0 || m === -0;
                                });
                              const allSeedingUnset =
                                step.criterion === GroupStandingsTieBreakerEnum.SEEDING &&
                                tieTeamIds.length > 0 &&
                                tieTeamIds.every((id) =>
                                  seedingMetricIsUnset(sg.metricsByTeamId[id]),
                                );
                              return (
                              <div key={j} className="mb-3">
                                <div className="small font-weight-bold">
                                  {t(listParticipantsOrTeams)}:{' '}
                                  {tieTeamIds
                                    .map((tid) => resolveTieStepEntityName(tid, teamIdToName, usePlayerLabels, t))
                                    .join(', ')}
                                </div>
                                {allSeedingUnset ? (
                                  <p className="small text-muted mb-2">
                                    {t('standingsExplain-seedingAllUnset')}
                                  </p>
                                ) : null}
                                {allH2hZero ? (
                                  <p className="small text-muted mb-2">
                                    {step.criterion === GroupStandingsTieBreakerEnum.HEAD_TO_HEAD_POINTS
                                      ? t('standingsExplain-h2hAllTiedPoints')
                                      : step.criterion === GroupStandingsTieBreakerEnum.HEAD_TO_HEAD_TEAM_WINS
                                        ? t('standingsExplain-h2hAllTiedTeamWins')
                                        : t('standingsExplain-h2hAllTiedWins')}
                                  </p>
                                ) : null}
                                {sg.note
                                  ? step.criterion === GroupStandingsTieBreakerEnum.COIN_TOSS
                                    ? (
                                        <div className="mb-2">
                                          <CoinTossSubgroupBlock
                                            note={sg.note}
                                            splitInto={sg.splitInto}
                                            nameById={teamIdToName}
                                            usePlayer={usePlayerLabels}
                                            t={t}
                                          />
                                        </div>
                                      )
                                    : (
                                        <div className="small text-muted mb-2">
                                          {humanizeTieStepNote(
                                            sg.note,
                                            teamIdToName,
                                            usePlayerLabels,
                                            t,
                                          )}
                                        </div>
                                      )
                                  : null}
                                {step.criterion === GroupStandingsTieBreakerEnum.COIN_TOSS &&
                                !coinTossHasDrawOrder ? (
                                  <p className="small text-muted mb-2">
                                    {t('standingsExplain-coinTossNoShuffleMeta')}
                                  </p>
                                ) : null}
                                <table className="table table-sm table-bordered mb-2">
                                  <thead>
                                    <tr>
                                      <th>{t(colParticipantOrTeam)}</th>
                                      <th>{tieStepSecondColumnHeader(step.criterion, t)}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {tieTeamIds.map((tid) => {
                                      const val = sg.metricsByTeamId[tid];
                                      let metric: { display: string; title?: string };
                                      if (
                                        step.criterion === GroupStandingsTieBreakerEnum.COIN_TOSS &&
                                        coinTossHasDrawOrder &&
                                        coinOrderIds
                                      ) {
                                        const pos = coinOrderIds.indexOf(tid);
                                        if (pos >= 0) {
                                          metric = {
                                            display: t('standingsExplain-metricCoinTossRank', {
                                              pos: pos + 1,
                                              total: coinOrderIds.length,
                                            }),
                                            title: t('standingsExplain-metricCoinTossRankTitle'),
                                          };
                                        } else {
                                          metric = formatTieStepMetricCell(
                                            val,
                                            step.criterion,
                                            t,
                                          );
                                        }
                                      } else {
                                        metric = formatTieStepMetricCell(
                                          val,
                                          step.criterion,
                                          t,
                                        );
                                      }
                                      return (
                                        <tr key={tid}>
                                          <td title={`ID ${tid}`}>
                                            {resolveTieStepEntityName(tid, teamIdToName, usePlayerLabels, t)}
                                          </td>
                                          <td title={metric.title}>{metric.display}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                                <div className="small">
                                  <strong>{t('standingsExplain-splitInto')}:</strong>{' '}
                                  {sg.splitInto.map((block) => formatTieSplitBlock(block, teamIdToName, usePlayerLabels, t)).join(' ')}
                                </div>
                              </div>
                              );
                            })}
                          </Accordion.Body>
                        </Accordion.Item>
                      ))}
                    </Accordion>

                    <h6 className="small font-weight-bold mt-3">{t('standingsExplain-finalRanking')}</h6>
                    <div className="table-responsive">
                      <table
                        className="table table-sm table-striped w-100 text-start"
                        style={groupStandingsTableStyle(showTeamSeriesColumns, showTeamPtsColumn)}
                      >
                        {groupStandingsColgroup(showTeamSeriesColumns, showTeamPtsColumn)}
                        <thead>
                          <tr>
                            <th className={ST_RANK}>#</th>
                            <th className={ST_TEAM}>{t(colParticipantOrTeam)}</th>
                            {showTeamSeriesColumns ? teamSeriesHeaderCells(t, showTeamPtsColumn) : null}
                            <th className={ST_STAT}>{t('standingsExplain-played')}</th>
                            <th className={ST_STAT}>
                              {t('standingsExplain-matchWins')}
                            </th>
                            <th className={ST_STAT}>
                              {t('standingsExplain-lost')}
                            </th>
                            <th className={ST_STAT}>{t('standingsExplain-gamesWon')}</th>
                            <th className={ST_STAT}>{t('standingsExplain-pointsDiff')}</th>
                            <th className={ST_STAT}>{t('standingsExplain-pointsFor')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeGroup.finalRanking.map((row) => (
                              <tr key={row.id}>
                                <td className={ST_RANK}>{row.position}</td>
                                <td className={ST_TEAM} title={row.name}>
                                  {row.name}
                                </td>
                                {showTeamSeriesColumns ? teamSeriesBodyCells(row, showTeamPtsColumn) : null}
                                <td className={ST_STAT}>{formatStandingsCell(row.countMatches)}</td>
                                <td className={ST_STAT}>{formatStandingsCell(row.matchWin)}</td>
                                <td className={ST_STAT}>{formatStandingsCell(matchLossForRow(row))}</td>
                                <td className={ST_STAT}>{formatStandingsCell(row.gameWin)}</td>
                                <td className={ST_STAT}>{formatStandingsCell(pointsDiffForRow(row))}</td>
                                <td className={ST_STAT}>{formatStandingsCell(row.pointsWon)}</td>
                              </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : null}
            </Tab>

            <Tab eventKey="elimination" title={t('standingsExplain-tabElimination')}>
                {!elimination?.supported ? (
                  <Alert variant="secondary">
                    {elimination?.message || elimination?.reason || t('standingsExplain-noElimination')}
                  </Alert>
                ) : (
                  <>
                    <p className="small">
                      <strong>{t('standingsExplain-seedingType')}:</strong>{' '}
                      {seedingTypeLabel(t, elimination.seedingType)}
                      <br />
                      <strong>{t('standingsExplain-eliminationDraw')}:</strong>{' '}
                      {eliminationDrawLabel(t, elimination.eliminationDraw)}
                      {elimination.hasConsolation ? (
                        <>
                          <br />
                          <strong>{t('standingsExplain-consolationDraw')}:</strong>{' '}
                          {consolationDrawLabel(t, elimination.consolationDraw)}
                        </>
                      ) : null}
                    </p>

                    {elimination.perGroupSummary?.length ? (
                      <>
                        <h6 className="small font-weight-bold">{t('standingsExplain-perGroupSummary')}</h6>
                        {elimination.perGroupSummary.map((row, i) => {
                          const groupName = (row.groupName as string) ?? String(i);
                          const advancingSet = new Set(
                            (row.advancingTeamIds as number[] | undefined) ?? [],
                          );
                          const standings = row.standings as StandingsStatsRow[] | undefined;
                          if (standings?.length) {
                            return (
                              <div key={i} className="mb-3">
                                <div className="small font-weight-bold mb-1">{groupName}</div>
                                <div className="table-responsive">
                                  <table
                                    className="table table-sm table-bordered small mb-0 w-100 text-start"
                                    style={groupStandingsTableStyle(showTeamSeriesColumns, showTeamPtsColumn)}
                                  >
                                    {groupStandingsColgroup(showTeamSeriesColumns, showTeamPtsColumn)}
                                    <thead>
                                      <tr>
                                        <th className={ST_RANK}>#</th>
                                        <th className={ST_TEAM}>{t(colParticipantOrTeam)}</th>
                                        {showTeamSeriesColumns ? teamSeriesHeaderCells(t, showTeamPtsColumn) : null}
                                        <th className={ST_STAT}>{t('standingsExplain-played')}</th>
                                        <th className={ST_STAT}>
                                          {t('standingsExplain-matchWins')}
                                        </th>
                                        <th className={ST_STAT}>
                                          {t('standingsExplain-lost')}
                                        </th>
                                        <th className={ST_STAT}>{t('standingsExplain-gamesWon')}</th>
                                        <th className={ST_STAT}>{t('standingsExplain-pointsDiff')}</th>
                                        <th className={ST_STAT}>{t('standingsExplain-pointsFor')}</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {standings.map((s) => {
                                        const highlight = advancingSet.has(s.id);
                                        return (
                                          <tr
                                            key={s.id}
                                            className={highlight ? 'table-success' : undefined}
                                          >
                                            <td className={ST_RANK}>{s.position}</td>
                                            <td className={ST_TEAM} title={s.name}>
                                              {s.name}
                                            </td>
                                            {showTeamSeriesColumns ? teamSeriesBodyCells(s, showTeamPtsColumn) : null}
                                            <td className={ST_STAT}>{formatStandingsCell(s.countMatches)}</td>
                                            <td className={ST_STAT}>{formatStandingsCell(s.matchWin)}</td>
                                            <td className={ST_STAT}>{formatStandingsCell(matchLossForRow(s))}</td>
                                            <td className={ST_STAT}>{formatStandingsCell(s.gameWin)}</td>
                                            <td className={ST_STAT}>{formatStandingsCell(pointsDiffForRow(s))}</td>
                                            <td className={ST_STAT}>{formatStandingsCell(s.pointsWon)}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            );
                          }
                          return (
                            <ul className="small" key={i}>
                              <li>
                                {groupName}: {t('standingsExplain-advancing')}{' '}
                                {(row.advancingTeamIds as number[])?.join(', ') ?? '—'}
                              </li>
                            </ul>
                          );
                        })}
                      </>
                    ) : null}

                    {elimination.mainBracket?.rankedList?.length ? (
                      <>
                        <h6 className="small font-weight-bold mt-3">
                          {t('standingsExplain-mainSeededList')}
                        </h6>
                        <div className="table-responsive border rounded overflow-hidden">
                          <table
                            className={SEEDED_LIST_TABLE_CLASS}
                            style={seededListTableStyle(showTeamSeriesColumns, showTeamPtsColumn)}
                          >
                            {seededListColgroup(showTeamSeriesColumns, showTeamPtsColumn)}
                            <thead>
                              <tr className="table-light">
                                <th className={ST_STAT}>{t('standingsExplain-bracketSeed')}</th>
                                <th className={ST_TEAM}>{t(colParticipantOrTeam)}</th>
                                <th className={ST_TEAM}>{t('standingsExplain-group')}</th>
                                <th className={ST_STAT}>{t('standingsExplain-groupRank')}</th>
                                {showTeamSeriesColumns ? teamSeriesHeaderCells(t, showTeamPtsColumn) : null}
                                <th className={ST_STAT}>{t('standingsExplain-played')}</th>
                                <th className={ST_STAT}>
                                  {t('standingsExplain-matchWins')}
                                </th>
                                <th className={ST_STAT}>
                                  {t('standingsExplain-lost')}
                                </th>
                                <th className={ST_STAT}>{t('standingsExplain-gamesWon')}</th>
                                <th className={ST_STAT}>{t('standingsExplain-pointsDiff')}</th>
                                <th className={ST_STAT}>{t('standingsExplain-pointsFor')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(
                                elimination.mainBracket.rankedList as Array<
                                  StandingsStatsRow & {
                                    seed?: number;
                                    groupName?: string;
                                  }
                                >
                              ).map((team, idx) => (
                                  <tr key={`${team.id}-${idx}`}>
                                    <td className={ST_STAT}>{formatStandingsCell(team.seed)}</td>
                                    <td className={ST_TEAM} title={team.name}>
                                      {team.name}
                                    </td>
                                    <td className={ST_TEAM} title={team.groupName ?? undefined}>
                                      {team.groupName ?? '—'}
                                    </td>
                                    <td className={ST_STAT}>{formatStandingsCell(team.position)}</td>
                                    {showTeamSeriesColumns ? teamSeriesBodyCells(team, showTeamPtsColumn) : null}
                                    <td className={ST_STAT}>{formatStandingsCell(team.countMatches)}</td>
                                    <td className={ST_STAT}>{formatStandingsCell(team.matchWin)}</td>
                                    <td className={ST_STAT}>{formatStandingsCell(matchLossForRow(team))}</td>
                                    <td className={ST_STAT}>{formatStandingsCell(team.gameWin)}</td>
                                    <td className={ST_STAT}>{formatStandingsCell(pointsDiffForRow(team))}</td>
                                    <td className={ST_STAT}>{formatStandingsCell(team.pointsWon)}</td>
                                  </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : null}

                    {elimination.consolationBracket?.rankedList?.length ? (
                      <>
                        <h6 className="small font-weight-bold mt-3">
                          {t('standingsExplain-consolationSeededList')}
                        </h6>
                        <div className="table-responsive border rounded overflow-hidden">
                          <table
                            className={SEEDED_LIST_TABLE_CLASS}
                            style={seededListTableStyle(showTeamSeriesColumns, showTeamPtsColumn)}
                          >
                            {seededListColgroup(showTeamSeriesColumns, showTeamPtsColumn)}
                            <thead>
                              <tr className="table-light">
                                <th className={ST_STAT}>{t('standingsExplain-bracketSeed')}</th>
                                <th className={ST_TEAM}>{t(colParticipantOrTeam)}</th>
                                <th className={ST_TEAM}>{t('standingsExplain-group')}</th>
                                <th className={ST_STAT}>{t('standingsExplain-groupRank')}</th>
                                {showTeamSeriesColumns ? teamSeriesHeaderCells(t, showTeamPtsColumn) : null}
                                <th className={ST_STAT}>{t('standingsExplain-played')}</th>
                                <th className={ST_STAT}>
                                  {t('standingsExplain-matchWins')}
                                </th>
                                <th className={ST_STAT}>
                                  {t('standingsExplain-lost')}
                                </th>
                                <th className={ST_STAT}>{t('standingsExplain-gamesWon')}</th>
                                <th className={ST_STAT}>{t('standingsExplain-pointsDiff')}</th>
                                <th className={ST_STAT}>{t('standingsExplain-pointsFor')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(
                                elimination.consolationBracket.rankedList as Array<
                                  StandingsStatsRow & {
                                    seed?: number;
                                    groupName?: string;
                                  }
                                >
                              ).map((team, idx) => (
                                  <tr key={`${team.id}-${idx}`}>
                                    <td className={ST_STAT}>{formatStandingsCell(team.seed)}</td>
                                    <td className={ST_TEAM} title={team.name}>
                                      {team.name}
                                    </td>
                                    <td className={ST_TEAM} title={team.groupName ?? undefined}>
                                      {team.groupName ?? '—'}
                                    </td>
                                    <td className={ST_STAT}>{formatStandingsCell(team.position)}</td>
                                    {showTeamSeriesColumns ? teamSeriesBodyCells(team, showTeamPtsColumn) : null}
                                    <td className={ST_STAT}>{formatStandingsCell(team.countMatches)}</td>
                                    <td className={ST_STAT}>{formatStandingsCell(team.matchWin)}</td>
                                    <td className={ST_STAT}>{formatStandingsCell(matchLossForRow(team))}</td>
                                    <td className={ST_STAT}>{formatStandingsCell(team.gameWin)}</td>
                                    <td className={ST_STAT}>{formatStandingsCell(pointsDiffForRow(team))}</td>
                                    <td className={ST_STAT}>{formatStandingsCell(team.pointsWon)}</td>
                                  </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : null}

                    {elimination.mainBracket?.matchups?.length ? (
                      <>
                        <h6 className="small font-weight-bold">{t('standingsExplain-mainMatchups')}</h6>
                        <ol className="small">
                          {(elimination.mainBracket.matchups as Array<{ home?: { name?: string }; away?: { name?: string } }>).map(
                            (m, i) => (
                              <li key={i}>
                                {m.home?.name ?? '?'} vs {m.away?.name ?? '?'}
                              </li>
                            ),
                          )}
                        </ol>
                      </>
                    ) : null}

                    {elimination.mainBracket?.trace?.length ? (
                      <>
                        <h6 className="small font-weight-bold">{t('standingsExplain-bracketTrace')}</h6>
                        <Accordion>
                          {(elimination.mainBracket.trace as Array<{ phase: string; message: string; details?: unknown }>).map(
                            (ev, idx) => (
                              <Accordion.Item eventKey={`t-${idx}`} key={idx}>
                                <Accordion.Header>
                                  <span className="text-capitalize">{ev.phase}</span>: {ev.message}
                                </Accordion.Header>
                                <Accordion.Body>
                                  <pre className="small mb-0 bg-white p-2 border rounded overflow-auto">
                                    {JSON.stringify(ev.details ?? {}, null, 2)}
                                  </pre>
                                </Accordion.Body>
                              </Accordion.Item>
                            ),
                          )}
                        </Accordion>
                      </>
                    ) : null}

                    {elimination.consolationBracket?.matchups?.length ? (
                      <>
                        <h6 className="small font-weight-bold mt-3">{t('standingsExplain-consolationMatchups')}</h6>
                        <ol className="small">
                          {(
                            elimination.consolationBracket.matchups as Array<{
                              home?: { name?: string };
                              away?: { name?: string };
                            }>
                          ).map((m, i) => (
                            <li key={i}>
                              {m.home?.name ?? '?'} vs {m.away?.name ?? '?'}
                            </li>
                          ))}
                        </ol>
                      </>
                    ) : null}
                  </>
                )}
            </Tab>
          </Tabs>
        </>
      ) : null}
    </div>
  );
};
